import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SmoothScroll } from "@/components/motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | pending | expired | error
  const [txn, setTxn] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setTxn(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        // Still pending — keep polling
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        setState("error");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <SmoothScroll>
      <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF] flex items-center justify-center px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl w-full"
          data-testid="subscription-success-page"
        >
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
            — Subscription
          </div>

          {state === "polling" && (
            <div data-testid="subscription-polling">
              <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
                Confirming your <span className="italic">payment…</span>
              </h1>
              <div className="mt-10 flex items-center gap-3 text-[#C9C6C6]">
                <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
                <span>Talking to Stripe. This usually takes a few seconds.</span>
              </div>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="subscription-paid">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-[#0E93EC] flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[#EEEEEF]" />
                </div>
              </div>
              <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
                You're in.
                <br />
                <span className="italic">Welcome to The Pros Work Hub.</span>
              </h1>
              <p className="mt-8 text-lg text-[#C9C6C6] leading-relaxed max-w-lg">
                Your <b className="text-[#EEEEEF] uppercase tracking-wide">{txn?.plan_id}</b> plan is
                now active. We'll email a receipt to{" "}
                <b className="text-[#EEEEEF]">{txn?.email}</b> and start routing
                local leads to your inbox.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate("/")}
                  className="group bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-8 pr-5 font-medium"
                  data-testid="subscription-home-cta"
                >
                  <span className="mr-2">Back to homepage</span>
                  <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Button>
              </div>
            </div>
          )}

          {state === "pending" && (
            <div data-testid="subscription-pending">
              <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
                Almost there.
              </h1>
              <p className="mt-8 text-lg text-[#C9C6C6] leading-relaxed max-w-lg">
                Your payment is still processing at Stripe's end. Feel free to
                close this page — we'll email {txn?.email} the moment it clears.
              </p>
              <Link
                to="/"
                className="mt-10 inline-flex items-center gap-2 text-[#0E93EC] font-medium border-b border-[#0E93EC] pb-1"
              >
                Back to homepage <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {state === "expired" && (
            <div data-testid="subscription-expired">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-[#0E93EC] flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
                Session <span className="italic">expired.</span>
              </h1>
              <p className="mt-8 text-lg text-[#C9C6C6] leading-relaxed max-w-lg">
                No charge was made. You can start again anytime from the pricing
                page.
              </p>
              <Link
                to="/#pricing"
                className="mt-10 inline-flex items-center gap-2 text-[#0E93EC] font-medium border-b border-[#0E93EC] pb-1"
              >
                Back to pricing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {state === "error" && (
            <div data-testid="subscription-error">
              <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
                Something went <span className="italic">sideways.</span>
              </h1>
              <p className="mt-8 text-lg text-[#C9C6C6] leading-relaxed max-w-lg">
                We couldn't verify your session. If you were charged, we'll sort
                it out — email hello@homeproleads.com.
              </p>
              <Link
                to="/"
                className="mt-10 inline-flex items-center gap-2 text-[#0E93EC] font-medium border-b border-[#0E93EC] pb-1"
              >
                Back to homepage <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </SmoothScroll>
  );
}
